import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  createComponent,
  createEnvironmentInjector,
  ElementRef,
  EnvironmentInjector,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { AppConfig } from '@app/core/app.config';
import { MapMarkersTypeEnum } from '@app/core/enums/map/map-markers-type.enum';
import { Asset } from '@app/core/model/entities/asset/asset';
import { EqualsMapMarkerConfig, RangeMapMarkerConfig } from '@app/core/model/entities/asset/map-marker';
import { FIELD_EXTRA_DATA } from '@app/core/model/other/field-config';
import {
  AssetsMapSidebarControlComponent
} from '@app/features/main/views/assets/assets-inventory/assets-map/asset-map-sidebar/assets-map-sidebar-control.component';
import {
  AssetsMapLegendControlComponent
} from '@app/features/main/views/assets/assets-inventory/assets-map/assets-map-legend-control/assets-map-legend-control.component';
import {
  AssetsMapTooltipComponent
} from '@app/features/main/views/assets/assets-inventory/assets-map/assets-map-tooltip/assets-map-tooltip.component';
import { MapMarkersService } from '@app/features/main/views/assets/assets-inventory/assets-map/map-markers.service';
import { AbstractAssetMapComponent } from '@app/shared/components/map/abstract-asset-map.component';
import { AssetMapMarker } from '@app/shared/components/map/asset-map-marker';
import { AssetMapMarkerGroup } from '@app/shared/components/map/asset-map-marker-group';
import { TranslateService } from '@ngx-translate/core';
import { AppManager } from '@services/managers/app.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import * as L from 'leaflet';
import { Point } from 'leaflet';

@Component({
  selector: 'leaflet',
  templateUrl: './leaflet.component.html',
  styleUrl: './leaflet.component.scss',
  providers: [
    MapMarkersService
  ],
  standalone: false
})
export class LeafletComponent extends AbstractAssetMapComponent implements OnDestroy, OnInit, OnChanges {

  protected readonly appConfig = inject(AppConfig);
  public readonly elementRef = inject(ElementRef);
  private readonly mapMarkersService = inject(MapMarkersService);
  private readonly snackbarManager = inject(SnackbarManager);
  private readonly injector = inject(EnvironmentInjector);
  private readonly appManager = inject(AppManager);
  protected readonly translate = inject(TranslateService);

  @Input() public assets: Asset[];

  @ViewChild('legendControl') public legendControl: AssetsMapLegendControlComponent;

  private assetsWithAddress: Asset[] = [];
  private selectedAssetMapMarker: AssetMapMarker;

  public readonly sidebar: L.Control.Sidebar;

  private snackbar: MatSnackBarRef<SimpleSnackBar>;

  public groupedMarkersEnable: boolean;
  public assetMapMarkerGroups: AssetMapMarkerGroup[];

  public currentAssetMapMarkerGroup: AssetMapMarkerGroup;

  private readonly groupMarkersSettingStorageKey: string;
  private readonly markerConfigStorageKey: string;

  constructor() {
    super();
    this.assetMapMarkerGroups = [];
    this.sidebar = L.control.sidebar(this.sidebarOptions);

    this.groupMarkersSettingStorageKey = 'assets-map-cluster-enable-' + this.accessManager.currentUser.id + '-'
      + this.appManager.currentOrganization.id;
    this.markerConfigStorageKey = 'assets-map-marker-config-' + this.accessManager.currentUser.id + '-'
      + this.appManager.currentOrganization.id;

    // Restore options from local storage
    this.groupedMarkersEnable = coerceBooleanProperty(localStorage.getItem(this.groupMarkersSettingStorageKey) ?? true);
  }

  public ngOnInit() {
    super.ngOnInit();

    // Get only assets with Addresses
    this.refreshAssetsWithAddress();

    // Show the Leaflet map if at least one set of coordinates was found
    const missingAddressCounter = this.assets.length - this.assetsWithAddress.length;
    if (missingAddressCounter === this.assets.length) {
      this.snackbar = this.snackbarManager.showInfiniteSnackbar(this.translate.instant('LABEL.NO_ASSET_COORDINATES_ALL'));
    } else {
      this.hasAddress = true;
      if (missingAddressCounter > 0) {
        this.snackbar = this.snackbarManager.showInfiniteSnackbar(
          this.translate.instant('LABEL.NO_ASSET_COORDINATES_SOME')
        );
      }
    }

    // Create the default assets' markers group
    this.setMapMarkers();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const assetsChange: SimpleChange = changes.assets;
    if (!assetsChange?.firstChange) {
      this.assetMapMarkerGroups.forEach(markerGroup => {
        markerGroup.clearAllMarkers();
      });
      if (assetsChange.currentValue) {
        this.refreshAssetsWithAddress();
        this.createAssetsMapMarkers(this.currentAssetMapMarkerGroup);
        this.currentAssetMapMarkerGroup.getFeatureGroup().addTo(this.map);
      }
      this.setInitialZoom();
    }
  }

  /**
   * Clear service subscriptions, event listeners and the map's layers before the destruction of the component
   */
  public ngOnDestroy(): void {
    if (this.snackbar) {
      this.snackbar.dismiss();
    }
    this.assetMapMarkerGroups.forEach(markerGroup => {
      markerGroup.clearAllMarkers();
    });

    if (this.map) {
      this.map.clearAllEventListeners();
      this.mapMarkersService.clearSubscriptions();
    }
  }

  /**
   * Event thrown by the map once it is loaded
   * @param leafletMap the map object
   */
  public onMapReady(leafletMap: L.Map): void {
    super.onMapReady(leafletMap);

    // Get stored configuration ID
    const storedConfig = localStorage.getItem(this.markerConfigStorageKey);

    // Get all map markers of the current Organization
    this.mapMarkersService.loadMapMarkersData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mapMarkers => {
        // Create a marker group for each configuration
        mapMarkers.forEach(mapMarker => {
          const group = new AssetMapMarkerGroup(
            mapMarker.name,
            this.markerClusterOptions,
            mapMarker
          );
          this.assetMapMarkerGroups.push(group);

          // If the map marker config corresponds to the one stored previously, set as current group
          if (mapMarker.id === storedConfig) {
            this.applyMapMarkerGroup(group);
          }
        });
      });
  }

  /**
   * Group or ungroup the map's markers according to the slide toggle's new value. Also store the setting in the
   * local storage for persistence.
   * @param enableClusters Whether to group markers.
   */
  public toggleClusterMode(enableClusters: boolean): void {
    this.groupedMarkersEnable = enableClusters;
    localStorage.setItem(this.groupMarkersSettingStorageKey, this.groupedMarkersEnable.toString());
    this.currentAssetMapMarkerGroup.setCluster(enableClusters);
    this.currentAssetMapMarkerGroup.getFeatureGroup().addTo(this.map);
  }

  /**
   * Change the markers on the map for the new group.
   * @param selectedMapMarkerGroup Selected layer group
   */
  public applyMapMarkerGroup(selectedMapMarkerGroup: AssetMapMarkerGroup): void {
    this.currentAssetMapMarkerGroup.getFeatureGroup().remove();
    selectedMapMarkerGroup.setCluster(this.groupedMarkersEnable);
    selectedMapMarkerGroup.clearAllMarkers();
    this.createAssetsMapMarkers(selectedMapMarkerGroup);
    selectedMapMarkerGroup.getFeatureGroup().addTo(this.map);
    this.currentAssetMapMarkerGroup = selectedMapMarkerGroup;

    // Store config
    localStorage.setItem(this.markerConfigStorageKey, selectedMapMarkerGroup.mapMarker?.id);
  }

  /**
   * Initiate the grouped overlay and create the default assets' markers group
   */
  protected setMapMarkers(): void {
    this.currentAssetMapMarkerGroup = new AssetMapMarkerGroup('assets', this.markerClusterOptions);
    this.currentAssetMapMarkerGroup.setCluster(this.groupedMarkersEnable);
    this.assetMapMarkerGroups.push(this.currentAssetMapMarkerGroup);
    this.defaultMarkerGroup = this.currentAssetMapMarkerGroup.getFeatureGroup();
    this.createAssetsMapMarkers(this.currentAssetMapMarkerGroup);
  }

  /**
   * Create a tile layer from each map provider
   * Add all tile layers to the minimap and add the minimap to the map
   */
  protected setBaseLayers(): void {
    const baseLayers: any = {
      'Voyager': L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19
      }),
      'WorldImagery': L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
        }
      ),
      'Openstreetmap': L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {maxZoom: 18, minZoom: 2})
    };

    // Add all tile layers to the minimap and add the minimap to the map
    const minimapOptions = {
      collapsed: true,
      position: 'topleft'
    };
    const minimap = L.control.layers.minimap(baseLayers, null, minimapOptions).addTo(this.map);
    // Remove the default leaflet layers icon of the control and replace it to a material design icon
    minimap.getContainer().firstElementChild.classList.add('mdi', 'mdi-layers', 'sm');
    // Add the default OSM to the map so something is selected by default
    baseLayers['Openstreetmap'].addTo(this.map);
  }

  /**
   * Create Markers for each asset with an address in a layer group based on a configuration.
   * If there is any configuration, all the markers have the default icon.
   * @param assetMapMarkerGroup a layer group used to add all the new markers in it
   */
  private createAssetsMapMarkers(assetMapMarkerGroup: AssetMapMarkerGroup): void {
    const defaultIconOptions = {
      iconSize: new Point(29, 46),
      iconAnchor: new Point(14.5, 41)
    };

    // Create Markers for each asset with an address
    this.assetsWithAddress.forEach((asset) => {
      const formattedLatitude = this.formatCoordinateToNumber(asset.properties['latitude']);
      const formattedLongitude = this.formatCoordinateToNumber(asset.properties['longitude']);

      const mapMarker = assetMapMarkerGroup.mapMarker;

      // Get the value of the asset property based on a path of the configuration
      const propertyValue = mapMarker ? mapMarker.property
        .split('.')
        .reduce((object, property) => object?.[property], asset) : null;

      // Get the icons' filenames based on the configuration
      const iconsConfig = mapMarker ?
        mapMarker.configs.find((config) => {
          switch (mapMarker.type) {
            case MapMarkersTypeEnum.MAP_MARKER_TYPE_EQUALS: {
              return propertyValue === (config as EqualsMapMarkerConfig).value;
            }
            case MapMarkersTypeEnum.MAP_MARKER_TYPE_RANGE: {
              return propertyValue >= (config as RangeMapMarkerConfig).valueMin && propertyValue <= (config as RangeMapMarkerConfig).valueMax;
            }
            default: {
              return false;
            }
          }
        }) : null;

      // If there is any icon's filenames found, the icon of the marker is default
      const markerIconName = iconsConfig ? iconsConfig.iconMarkerFileName : mapMarker?.defaultIconMarkerFileName;
      const markerIcon: L.Icon = L.icon({
        iconUrl: mapMarker ? `assets/images/map/${markerIconName}.png` : this.defaultIconMarkerUrl,
        ...defaultIconOptions
      });

      // If there is any icon's filename found, the icon of the selected marker is default
      const selectedMarkerIconName = iconsConfig ? iconsConfig.iconSelectedMarkerFileName : mapMarker?.defaultIconSelectedMarkerFileName;
      const selectedMarkerIcon: L.Icon = L.icon({
        iconUrl: mapMarker ? `assets/images/map/${selectedMarkerIconName}.png`
          : this.defaultIconMarkerSelectedUrl,
        ...defaultIconOptions
      });

      // Create the asset's maker and a custom tooltip in it
      const tooltip = L.tooltip({direction: 'top', offset: [0, -41], interactive: true})
        .setContent(this.createCustomTooltip(asset.name, mapMarker?.propertyValueMessage, propertyValue));
      const currentAssetMarker = new AssetMapMarker(
        L.latLng(formattedLatitude, formattedLongitude),
        {icon: markerIcon},
        asset.id, markerIcon,
        selectedMarkerIcon
      )
        .bindTooltip(tooltip);

      // If asset marker of a possibly existed previous overlay is selected, select the new marker with the same asset ID
      if (currentAssetMarker.assetId === this.selectedAssetMapMarker?.assetId) {
        currentAssetMarker.select();
        this.selectedAssetMapMarker = currentAssetMarker;
      }

      // Event when the marker is clicked
      currentAssetMarker.on({
        preclick: (() => {
          if (this.selectedAssetMapMarker === currentAssetMarker) {
            return;
          }

          this.selectedAssetMapMarker = currentAssetMarker;

          // Select only markers with the same asset ID in all the existed overlays
          Object.values(this.assetMapMarkerGroups).forEach(markerGroup => {
            markerGroup.selectMarker(currentAssetMarker.assetId);
          });

          // Add the sidebar back to the map
          this.sidebar.addTo(this.map);
        }),
        click: (() => {
          this.sidebar.removePanel('assetInfo');
          // Change colour
          const panelContent: L.PanelOptions = {
            id: 'assetInfo',
            tab: '<span class="mdi mdi-close sm"></span>',
            pane: this.createCustomSidebar(asset),
            position: 'top'
          };

          // Add the created panel to the sidebar
          this.sidebar.addPanel(panelContent)
            .on({
              closing: () => {
                // Unselect all markers
                this.selectedAssetMapMarker = null;
                Object.values(this.assetMapMarkerGroups).forEach(markerGroup => {
                  markerGroup.unSelectAllMarkers();
                });
                // Remove the sidebar
                this.sidebar.remove();
              }
            })
            .open('assetInfo');
        })
      });

      // Add the new marker in the layer group
      assetMapMarkerGroup.getFeatureGroup().addLayer(currentAssetMarker);
    });
  }

  /**
   * Create a custom sidebar based on an Asset
   * @param asset used by the component to create the sidebar
   * @return HTMLElement the native HTML element of the sidebar component
   */
  private createCustomSidebar(asset: Asset): HTMLElement {
    const component = createComponent(AssetsMapSidebarControlComponent, {
      environmentInjector: createEnvironmentInjector([{
        provide: FIELD_EXTRA_DATA,
        useValue: {
          asset: asset
        }
      }], this.injector)
    });

    // Manually invoke change detection, automatic won't work, but this is Ok if the component doesn't change
    component.changeDetectorRef.detectChanges();

    /*
    NOTE: Structure required to get the necessary hooks on the pane, even if they go unused
     Using outerHTML or innerHTML will prevent from using angular features in the created component
     */
    const el = L.DomUtil.create('DIV', 'leaflet-sidebar-pane');
    el.appendChild<HTMLElement>(L.DomUtil.create('DIV'));
    el.appendChild<HTMLElement>(component.location.nativeElement);
    return el;
  }

  /**
   * Create a custom tooltip
   * @param assetName title of the tooltip
   * @param propertyValueMessage message of the property displayed in the tooltip
   * @param propertyValue value of the property
   * @return HTMLElement the native HTML element of the tooltip component
   */
  private createCustomTooltip(
    assetName: string,
    propertyValueMessage: string = null,
    propertyValue: string = null
  ): HTMLElement {
    const component = createComponent(AssetsMapTooltipComponent, {
      environmentInjector: createEnvironmentInjector([{
        provide: FIELD_EXTRA_DATA,
        useValue: {
          assetName: assetName,
          propertyValueMessage: propertyValueMessage,
          propertyValue: propertyValue
        }
      }], this.injector)
    });

    // Manually invoke change detection, automatic won't work, but this is Ok if the component doesn't change
    component.changeDetectorRef.detectChanges();
    return component.location.nativeElement;
  }

  /**
   * Refresh an array to get a list of asset with addresses
   */
  private refreshAssetsWithAddress(): void {
    this.assetsWithAddress = this.assets.filter(asset => asset.properties['latitude'] && asset.properties['longitude']);
  }

}
